import { api } from './api'
import type { OCRResult } from '../store/types'


/**
 * Response from finch-pay-api /api/v1/customer/receipts/parse endpoint
 */
interface ReceiptParseResponse {
  receipt_id: string
  status: string
  confidence: {
    score: number
    level: string
  }
  parsed_data: {
    amount_cents: number
    tax_cents: number | null
    subtotal_cents: number | null
    tip_cents: number | null // Added tip_cents
    currency: string
    merchant_name: string | null
    date_on_receipt: string | null
  }
  tip_suggestions: Array<{
    percent: number
    amount_cents: number
    display: string
  }>
  image_url: string
  image_url_expires_at: string
  requires_review: boolean
  message: string
  items: Array<{
    id: string
    name: string
    unit_price_cents: number
    quantity: number
    total_cents: number
    currency: string
    category: string | null
    position: number
  }>
  items_validation: {
    status: string | null
    items_total_cents: number | null
  }
  company: {
    name: string
  } | null // Added company object
}

/**
 * Convert cents to dollars
 */
function centsToDollars(cents: number | null | undefined): number {
  return cents ? cents / 100 : 0
}

/**
 * Extract receipt data using finch-pay-api
 *
 * @param file - Image file to parse
 * @param venueId - Venue ID
 * @returns Parsed receipt data
 */
export async function extractReceiptData(
  file: File,
  venueId: string,
): Promise<OCRResult> {
  // Create form data for multipart upload
  const formData = new FormData()
  formData.append('file', file)

  const data = await api.post<ReceiptParseResponse>('receipts/parse', formData, {
    headers: {
      'X-Venue-ID': venueId,
    },
  })

  // Map API response to store's OCRResult format
  return {
    receiptId: data.receipt_id,
    venue: {
      name: data.company?.name || data.parsed_data.merchant_name || 'Unknown Venue',
      address: '', // API doesn't extract address
    },
    items: data.items.map((item) => ({
      name: item.quantity !== 1 ? `${item.quantity}x ${item.name}` : item.name,
      price: centsToDollars(item.total_cents),
    })),
    subtotal: centsToDollars(data.parsed_data.subtotal_cents),
    tax: centsToDollars(data.parsed_data.tax_cents),
    total: centsToDollars(data.parsed_data.amount_cents),
    tipIncluded: (data.parsed_data.tip_cents || 0) > 0,
    tipAmount: centsToDollars(data.parsed_data.tip_cents),
    confidence: data.confidence.score,
    requiresReview: data.requires_review,
    tipSuggestions: data.tip_suggestions.map((tip) => ({
      percent: tip.percent,
      amount: centsToDollars(tip.amount_cents),
      display: tip.display,
    })),
  }
}
